<template>
  <div class="block-wizard">
    <div class="block-wizard__main field">
      <div class="subheader-container" v-if="!loading">
        <h1>{{ $t('survey.title') }}</h1>
        <div class="tools-form" v-if="emailVerified">
          <div class="filter-field">
            <label for="filterInput">{{ $t('survey.filter') }}</label>
            <input id="filterInput" type="text" v-model="filter" :placeholder="$t('survey.filterPlaceholder')" />
            <div class="clear" v-if="filter" @click="clearFilter">
              <span class="fa fa-times"></span>
            </div>
          </div>
          <div class="sort-field">
            <label for="sortSelect">{{ $t('survey.sortBy') }}</label>
            <div>
              <select id="sortSelect" v-model="sortKey" @change="sortChanged">
                <option value="name">{{ $t('survey.sort.name') }}</option>
                <option value="startedDate">{{ $t('survey.sort.startedDate') }}</option>
                <option value="updatedAt">{{ $t('survey.sort.updatedAt') }}</option>
              </select>
              <a href="" @click.prevent="toggleSort" style="margin-left: 1em">
                <span v-if="sortKey === 'name'" :class="{ 'fa': true, 'fa-sort-alpha-asc': sortAsc, 'fa-sort-alpha-desc': !sortAsc }"></span>
                <span v-else :class="{ 'fa': true, 'fa-long-arrow-up': sortAsc, 'fa-long-arrow-down': !sortAsc }"></span>
              </a>
            </div>
          </div>
        </div>
      </div>
      <error-boundary v-show="errorAlert" v-on:alert-error-closed="errorAlert = false, errorMessage=''" v-bind:errorMessage="errorMessage"></error-boundary>
      <div v-if="loading" class="block-wizard__loading">
        <LoadingComponent :text="$t('survey.loadingInProgress')"></LoadingComponent>
      </div>
      <div v-else-if="!emailVerified">
        <p>{{ $t('survey.signupEmailNotVerified', {email: $auth.getUserInfo().email}) }}</p>
        <div class="block-text-image__button">
          <a href="#" class="btn btn-fullwidth btn-gradient" @click="goToHome">{{ $t('app.homePage') }}</a>
        </div>
      </div>
      <template v-else>
        <a @click.prevent="newSurvey" class="btn-add only-sm" style="margin-top : 1em;">
          <span class="fa fa-plus"></span> {{ $t('survey.newEnterprise') }}
        </a>
        <div v-if="!$lodash.isEmpty(sectorsList)" style="margin-top: 1em;">
          <div class="c-message c-message--success" v-if="firstSurveyCreated">
            <p>{{ $t('survey.creationOk') }}</p>
          </div>
          <template v-if="filteredSurveySessionPagined.length">
            <div class="survey-list-title">
              <h2>{{ $t('survey.allSurveys') }}</h2>
              <div class="survey-list-actions">
                <a @click.prevent="newSurvey" class="btn-add">
                  <span class="fa fa-plus"></span> {{ $t('survey.newSurvey') }}
                </a>
              </div>
            </div>
            <div class="survey-list">
              <div class="survey-entries">
                <ProfileEntry
                  v-for="item in filteredSurveySessionPagined"
                  :key="item._id"
                  :enterpriseName="item.Name"
                  :enterpriseNumber="item.EnterpriseNumber"
                  :activityName="getActivityNameFromCode(item.SectorCode)"
                  :surveys="item.surveys"
                  :disabled="loading"
                  :serviceUnavailable="!enabledGoToSurvey"
                  :lastLoginAt="lastLoginAt"
                  @continueSurvey="(id, namespace) => redirectToNetSurvey(id, namespace)"
                  @viewResult="(id, namespace) => redirectToResult(item._id, id, namespace)"
                  @viewPDFResult="(surveyId) => redirectToPrint(item._id, surveyId)"
                  @editData="editYEnterpriseSectorUser(item._id)"
                  @shareData="(survey) => openShareModal(item, survey)"
                  @compareData="(survey) => openCompareModal(item, survey)"
                />
                <Paginate
                  v-if="pageCount > 0"
                  :pageCount="pageCount"
                  :pageRange="6"
                  :clickHandler="pageChange"
                  prevText="<span class='fa fa-chevron-left'></span>"
                  nextText="<span class='fa fa-chevron-right'></span>"
                  containerClass="pagination-container"
                  pageClass="page-number"
                  nextClass="page-nav"
                  prevClass="page-nav"
                  v-model="currentPage"
                >
                </Paginate>
              </div>
            </div>
          </template>
          <div class="empty-state" v-else>
            <p>{{ $t('survey.noResult') }}</p>
            <a @click.prevent="newSurvey" class="btn-add">
              <span class="fa fa-plus"></span> {{ $t('survey.newSurvey') }}
            </a>
          </div>

          <div class="c-message c-message--info" v-if="firstSurveyCreated">
            <span>Info</span>
            <p v-html="$t('survey.contextualisation')"></p>
          </div>
        </div>
        <Modal v-if="showShareModal && surveyInModal !== null" @close="closeShareModal" :height="modalHeight">
          <template slot="header">
            <h2 class="modal-title">{{ $t('survey.share') }} <span class="emphase">{{ surveyInModalName }}</span></h2>
          </template>
          <template slot="body">
            <p>{{ $t('survey.shareDataExplain') }}<br />{{ $t('survey.shareDataExplainMaximum') }}</p>
            <ShareList :partners="surveyInModalPartners"></ShareList>
          </template>
          <template slot="footer">
            <div class="block-cta__content-button modal-btn">
              <button class="btn btn-confirm" @click="updateSurveyInModalPartners">Confirmer</button>
            </div>
            <div class="block-cta__content-button modal-btn">
              <button class="btn btn-gray" @click="closeShareModal">{{ $t("survey.cancel") }}</button>
            </div>
          </template>
        </Modal>
        <Modal v-if="showCompareModal && surveyInModal !== null" @close="closeCompareModal" :height="modalHeight" class="compareModal">
          <template slot="header">
            <h2 class="modal-title">{{ $t('survey.compare') }} <span class="emphase">{{ surveyInModalName }}</span></h2>
          </template>
          <template slot="body">
            <p>{{ $t('survey.compareExplain') }}</p>
            <CompareList
              :surveySessionId="surveyInModal.sessionId"
              :surveySessions="allYEnterpriseSectorUsers"
              :namespace="surveyInModal.namespace"
              @change="onCompareSessionIdChange">
            </CompareList>
          </template>
          <template slot="footer">
            <div class="block-cta__content-button modal-btn">
              <button class="btn btn-confirm" @click="compareSurvey" :disabled="!compareToSessionId">Confirmer</button>
            </div>
            <div class="block-cta__content-button modal-btn">
              <button class="btn btn-gray" @click="closeCompareModal">{{ $t("survey.cancel") }}</button>
            </div>
          </template>
        </Modal>
      </template>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import {
  format, compareDesc, compareAsc, parseISO, fromUnixTime, isValid,
} from 'date-fns';
import ErrorBoundary from '@/components/ErrorBoundary.vue';
import ProfileEntry from '@/components/SurveyList/ProfileEntry.vue';
import ShareList from '@/components/SurveyList/ShareList.vue';
import Modal from '@/components/Modal.vue';
import errorBoundary from '@/mixins/errorBoundary';
import LoadingComponent from '@/components/Loading.vue';
import { listYEnterpriseSectorUsers, garnishYEnterpriseSectorUsers } from '@/providers/yEnterpriseSectorUsers';
import { updateSurveySessionSharedWith } from '@/providers/surveySession';
import { getSectors } from '@/providers/sectors';
import { listDigiscorePartners } from '@/providers/partners';
import { getAwsMonitoring } from '@/providers/awsMonitoring';
import Paginate from 'vuejs-paginate';
import CompareList from '@/components/SurveyList/CompareList.vue';

export default {
  name: 'Survey',
  mixins: [errorBoundary],
  components: {
    ErrorBoundary,
    LoadingComponent,
    ProfileEntry,
    Paginate,
    Modal,
    ShareList,
    CompareList,
  },
  data() {
    return {
      allYEnterpriseSectorUsers: [],
      firstSurveyCreated: false,
      loading: true,
      sectors: [],
      sectorsList: [],
      emailVerified: true,
      enabledGoToSurvey: true,
      filter: '',
      sortKey: 'updatedAt',
      sortAsc: false,
      itemPerPage: 10,
      currentPage: 1,
      lastLoginAt: '2021-04-01T10:40:18.498Z',
      showShareModal: false,
      showCompareModal: false,
      surveyInModal: null,
      surveyYEnterpriseSectorUser: null,
      surveyInModalName: '',
      surveyInModalPartners: [],
      compareToSessionId: '',
      allPartners: [],
      modalHeight: 0,
      scrollXFrom: 0,
      scrollYFrom: 0,
    };
  },
  watch: {
    filter() {
      this.currentPage = 1;
      window.localStorage.filter = this.filter;
    },
  },
  async created() {
    const isAuthenticated = await this.$auth.waitForSession();

    // Redirect to home if not authenticated
    if (!isAuthenticated) {
      this.$router.push('/');
      return;
    }

    this.emailVerified = this.$auth.getUserInfo().email_verified;
    this.firstSurveyCreated = this.$route.query.firstSurveyCreated ?? false;

    await Promise.all([
      (async () => {
        const { data: sectors } = await getSectors();
        if (!_.isEmpty(sectors)) {
          this.sectorsList = sectors;
          this.sectorsList = _.sortBy(this.sectorsList, 'Code');
        }
      })(),
      (async () => {
        const { data: responseApiYEnterprise } = await listYEnterpriseSectorUsers();

        if (_.isEmpty(responseApiYEnterprise) && this.emailVerified) {
          this.$router.push('/survey/start');
        }

        this.allYEnterpriseSectorUsers = responseApiYEnterprise;
      })(),
      (async () => {
        const { data: reponseApiPartners } = await listDigiscorePartners();
        this.allPartners = reponseApiPartners;
      })(),
    ]);

    this.lastLoginAt = this.getCookie('lastLogin');

    this.filter = (window.localStorage.filter !== undefined) ? window.localStorage.filter : '';
    await this.$nextTick();
    this.sortKey = (window.localStorage.sortKey !== undefined) ? window.localStorage.sortKey : 'updatedAt';
    await this.$nextTick();
    this.sortAsc = (window.localStorage.sortAsc !== undefined) ? window.localStorage.sortAsc === 'true' : false;
    this.currentPage = (window.localStorage.page !== undefined) ? Number.parseInt(window.localStorage.page, 10) : 1;

    this.loading = false;
  },
  async mounted() {
    try {
      const { data: getMetricAlarms } = await getAwsMonitoring();
      if (_.get(getMetricAlarms, 'MetricAlarms')) {
        _.forEach(_.get(getMetricAlarms, 'MetricAlarms'), (value) => {
          if (value.StateValue !== 'OK') {
            this.enabledGoToSurvey = false;
          }
        });
      }
    } catch (err) {
      this.displayError('retrieveMetricAlarms');
    }
  },
  methods: {
    goToHome() {
      this.$auth.logout({
        returnTo: window.location.origin,
      });
    },
    async redirectToNetSurvey(surveySessionId, Namespace) {
      console.log('redirectToNetSurvey', surveySessionId, Namespace);
      this.loading = true;

      // Récupération d'un accès à Ethnos
      try {
        const { data } = await garnishYEnterpriseSectorUsers({
          Namespace,
          id: surveySessionId,
        });
        if (_.get(data, 'EthnosURL')) {
          window.location.href = `${_.get(data, 'EthnosURL')}`;
        }
      } catch (err) {
        this.displayError('retrieveEthnosSurveySession');
      }
    },
    async redirectToResult(yEnterpriseSectorUserId, sessionId, namespace) {
      console.log('redirectToResult', yEnterpriseSectorUserId, sessionId, namespace);
      this.loading = true;
      return this.$router.push({
        path: `/survey/${namespace}/${yEnterpriseSectorUserId}/${sessionId}`,
      });
    },
    async redirectToPrint(YEnterpriseSectorUserId, surveySessionId) {
      this.loading = true;
      const token = await this.$auth.waitForToken();
      const surveySessionData = await axios.get(`${process.env.VUE_APP_DIGISCORE_API}/yEnterpriseSectorUserSurveySession/${YEnterpriseSectorUserId}/${surveySessionId}`);
      const enterprise = _.get(surveySessionData, 'data.yEnterpriseSector');
      const YEnterpriseSectorUser = { data: enterprise };
      const params = new URLSearchParams({
        ref: `${window.location.origin}/surveyPDF/${_.get(surveySessionData, 'data.Namespace')}/${_.get(YEnterpriseSectorUser, 'data._id')}/${surveySessionId}?token=${token}`,
        enterpriseName: _.get(surveySessionData, 'data.enterpriseName'),
      });
      this.loading = false;
      window.open(`${process.env.VUE_APP_DIGISCORE_API}/pdf?${params.toString()}`, '_blank');
    },
    editYEnterpriseSectorUser(yEnterpriseSectorUserId) {
      this.$router.push(`/survey/edit/${yEnterpriseSectorUserId}`);
    },
    newSurvey() {
      this.$router.push('/survey/start');
    },
    convertDate(d) {
      let date;
      if (!_.isEmpty(d)) {
        date = format(new Date(d), 'dd/MM/yyyy');
        return date;
      }
      return date;
    },
    getActivityNameFromCode(activityCode) {
      const sector = this.sectorsList.find((s) => s.Code === activityCode);
      return (sector !== undefined) ? sector.Name : '';
    },
    clearFilter() {
      this.filter = '';
      window.localStorage.filter = this.filter;
    },
    toggleSort() {
      this.sortAsc = !this.sortAsc;
      window.localStorage.sortAsc = this.sortAsc;
    },
    sortChanged() {
      this.sortAsc = this.sortKey === 'name';
      window.localStorage.sortAsc = this.sortAsc;
      window.localStorage.sortKey = this.sortKey;
    },
    pageChange(pageNum) {
      this.currentPage = pageNum;
      window.localStorage.page = this.currentPage;
    },
    getCookie(cname) {
      const name = `${cname}=`;
      const decodedCookie = decodeURIComponent(document.cookie);
      const ca = decodedCookie.split(';');
      for (let i = 0; i < ca.length; i += 1) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
          return c.substring(name.length, c.length);
        }
      }
      return '';
    },
    openCompareModal(yEnterpriseSectorUser, survey) {
      const clientRects = document.getElementsByClassName('home')[0].getClientRects();
      this.modalHeight = clientRects[0].height;

      this.scrollXFrom = window.scrollX;
      this.scrollYFrom = window.scrollY;
      window.scrollTo(0, 0);


      this.surveyInModal = survey;
      this.surveyInModalName = yEnterpriseSectorUser.Name;
      this.surveyYEnterpriseSectorUser = yEnterpriseSectorUser;
      this.showCompareModal = true;
    },
    closeCompareModal() {
      this.showCompareModal = false;
      this.surveyInModal = null;
      this.surveyYEnterpriseSectorUser = null;
      this.surveyInModalName = '';
      this.compareToSessionId = '';

      window.scrollTo(this.scrollXFrom, this.scrollYFrom);
      this.scrollXFrom = 0;
      this.scrollYFrom = 0;
    },
    onCompareSessionIdChange(sessionId) {
      this.compareToSessionId = sessionId;
    },
    compareSurvey() {
      if (!this.compareToSessionId || !this.surveyInModal) {
        return null;
      }
      this.loading = true;
      return this.$router.push({
        path: `/survey-compare/${this.surveyInModal.namespace}/${_.get(this.surveyYEnterpriseSectorUser, '_id')}/${this.surveyInModal.sessionId}/${this.compareToSessionId}`,
      });
    },
    openShareModal(yEnterpriseSectorUser, survey) {
      console.log('openShareModal', yEnterpriseSectorUser, survey);
      const clientRects = document.getElementsByClassName('home')[0].getClientRects();
      this.modalHeight = clientRects[0].height;

      this.scrollXFrom = window.scrollX;
      this.scrollYFrom = window.scrollY;
      window.scrollTo(0, 0);

      this.surveyInModal = survey;
      this.surveyInModalName = yEnterpriseSectorUser.Name;
      this.surveyInModalPartners = this.allPartners.map((partner) => ({
        ...partner, active: survey.sharedWith?.includes(partner.id),
      }));
      this.showShareModal = true;
    },
    closeShareModal() {
      this.showShareModal = false;
      this.surveyInModal = null;
      this.surveyInModalPartners = [];
      this.surveyInModalName = '';
      window.scrollTo(this.scrollXFrom, this.scrollYFrom);
      this.scrollXFrom = 0;
      this.scrollYFrom = 0;
    },
    async updateSurveyInModalPartners() {
      this.surveyInModal.sharedWith = this.surveyInModalPartners.filter((partner) => partner.active).map((partner) => partner.id);
      await updateSurveySessionSharedWith(this.surveyInModal);
      this.closeShareModal();
    },
  },
  computed: {
    filteredSurveySession() {
      return this.allYEnterpriseSectorUsers
        .filter((yesu) => {
          if (this.filter) {
            return yesu.Name?.toLowerCase().indexOf(this.filter.toLowerCase()) >= 0
              || yesu.surveys?.some((survey) => survey.username.toLowerCase().indexOf(this.filter.toLowerCase()) >= 0)
              || yesu.EnterpriseNumber?.toString().indexOf(this.filter.toLowerCase()) >= 0;
          }
          return true;
        })
        .sort((a, b) => {
          let dateA;
          let dateB;
          if (this.sortKey === 'updatedAt') {
            dateA = (Array.isArray(a.surveys) && a.surveys.length > 0) ? parseISO(a.surveys[0].lastUpdatedAt) : undefined;
            dateB = (Array.isArray(b.surveys) && b.surveys.length > 0) ? parseISO(b.surveys[0].lastUpdatedAt) : undefined;
          } else if (this.sortKey === 'startedDate') {
            dateA = (Array.isArray(a.surveys) && a.surveys.length > 0) ? parseISO(a.surveys[0].startedDate) : undefined;
            dateB = (Array.isArray(b.surveys) && b.surveys.length > 0) ? parseISO(b.surveys[0].startedDate) : undefined;
          }

          if (!isValid(dateA)) {
            dateA = fromUnixTime(0);
          }
          if (!isValid(dateB)) {
            dateB = fromUnixTime(0);
          }

          switch (this.sortKey) {
            case 'updatedAt':
              return (this.sortAsc)
                ? compareAsc(dateA, dateB)
                : compareDesc(dateA, dateB);
            case 'startedDate':
              return (this.sortAsc)
                ? compareAsc(dateA, dateB)
                : compareDesc(dateA, dateB);
            default:
            case 'name':
              return (this.sortAsc)
                ? a.Name.toLowerCase().localeCompare(b.Name.toLowerCase())
                : b.Name.toLowerCase().localeCompare(a.Name.toLowerCase());
          }
        });
    },
    filteredSurveySessionPagined() {
      const startIndex = (this.currentPage - 1) * this.itemPerPage;
      return this.filteredSurveySession.slice(startIndex, startIndex + this.itemPerPage);
    },
    pageCount() {
      return Math.ceil(this.filteredSurveySession.length / this.itemPerPage);
    },
  },
};
</script>

<style>
.block-wizard__main .content h6{
  color: #2368a1;
  font-weight: 700;
  margin-top: 32px;
  font-size: 1.1em;
}
.field label {
  margin-right: 40px;
}
.divButtonSurvey {
  margin-top: 30px;
}
.c-message--success {
  color: #6f9048;
}
.grey {
  color: #cccccc;
}
button:disabled {
  cursor: not-allowed;
  pointer-events: all !important;
}
h1 {
  font-size: 44px;
}
h2 {
  font-size: 32px;
  color: #1C1E1F;
  margin: 36px 0;
}
.survey-list {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
}
.survey-entries {
  flex: 1;
}
.survey-list-title {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 1em;
}
.btn-add {
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: #D0E9B3;
  border: 1px solid #94BB66;
  font-size: 16px;
  font-weight: bold;
  color: #6F9048;
  padding: 14px 16px;
}
.btn-add .fa {
  color: white;
  background-color: #6F9048;
  font-size: 18px;
  width: 24px;
  padding: 3px 0;
  text-align: center;
  border-radius: 50%;
  margin-right: .5em;
}
.btn-add:hover {
  color: #6F9048;
  background-color: #dffac0;
}
.subheader-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}
.tools-form {
  display: flex;
  flex-direction: row;
}
.tools-form > * {
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: flex-end;
}
.tools-form > * > label {
  margin: 0 0 7px 0;
  color: #2B3F3F;
  font-size: 14px;
  font-weight: bold;
}
.tools-form > * > select {
  background-color: white;
}
.tools-form .sort-field {
  min-width: 320px;
  margin-left: 1.5em;
}
.tools-form .sort-field > div {
  display: flex;
  align-items: center;
}
.tools-form .filter-field {
  position: relative;
  min-width: 380px;
}
.filter-field input {
  padding-right: 35px;
}
.filter-field .clear {
  position: absolute;
  right: 10px;
  bottom: .5em;
  cursor: pointer;
  font-size: 20px;
  color: #D7432B;
}

.pagination-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2em 0;
}
.page-number {
  text-align: center;
  line-height: 48px;
  font-size: 16px;
  font-weight: bold;
  border: 1px solid #EEEEEE;
  margin: 0 6px;
}
.page-number a {
  display: block;
  width: 48px;
  height: 48px;
  color: #575756;
}
.page-number.active {
  background-color: #0D93D2;
}
.page-number.active a {
  color: white;
}
.page-number:not(.active) a:hover {
  color: black;
  background-color: #e4f7ff;
}
.page-nav {
  font-size: 18px;
  margin: 0 12px;
}
.empty-state {
  width: 100%;
  min-height: 400px;
  background-color: #EEEEEE;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 32px;
}
.empty-state p {
  text-align: center;
  max-width: 400px;
  color: #1C1E1F;
  font-size: 32px;
}
.only-sm {
  display: none;;
}
.compareModal .modal-body {
 overflow: hidden;
}
.compareModal {
  position: fixed;
}

@media only screen and (max-width: 1024px) {
  .survey-list {
    flex-direction: column;
  }
  .subheader-container {
    flex-direction: column;
    align-items: flex-start;
  }
  .tools-form .sort-field {
    min-width: auto;
  }
  .tools-form .filter-field {
    min-width: auto;
  }
  .only-sm {
    display: block;
  }
}
.modal-btn {
  width: auto;
  padding: 0 1em;
}
.modal-title {
  margin: 36px 0 0 0;
}
.modal-title .emphase {
  color: #0C7DB3;
}
</style>
